import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../lib/api';

interface BalanceResponse {
  balanceCredits: number;
  totalSpendableCredits: number;
  monthlyRemainingCredits: number;
  topUpBalanceCredits: number;
  periodUsageCredits: number;
  periodAllowanceCredits: number;
  periodStart: string | null;
  periodEnd: string | null;
  lifetimeCredits: number;
  updatedAt: string;
  referralBonusCredits: number;
  basePlanCredits: number;
}

async function fetchBalance(): Promise<BalanceResponse> {
  const response = await fetch(`${API_URL}/api/v2/cloud/balance`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch balance');
  }

  return response.json();
}

export function useCreditBalance() {
  const { data, isLoading } = useQuery({
    queryKey: ['creditBalance'],
    queryFn: fetchBalance,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });

  const periodAllowance = data?.periodAllowanceCredits ?? 0;
  const periodUsage = data?.periodUsageCredits ?? 0;
  const creditsRemaining = data?.totalSpendableCredits ?? 0;
  const monthlyRemaining = data?.monthlyRemainingCredits ?? 0;
  const topUpCredits = data?.topUpBalanceCredits ?? 0;

  const usagePercent = periodAllowance > 0
    ? Math.min(100, Math.round((periodUsage / periodAllowance) * 100))
    : 0;

  const referralBonusCredits = data?.referralBonusCredits ?? 0;
  const basePlanCredits = data?.basePlanCredits ?? 0;

  return {
    balance: data?.balanceCredits ?? null,
    isLoading,
    periodAllowance,
    creditsRemaining,
    monthlyRemaining,
    topUpCredits,
    periodStart: data?.periodStart ?? null,
    periodEnd: data?.periodEnd ?? null,
    usagePercent,
    isLow: usagePercent >= 80,
    referralBonusCredits,
    basePlanCredits,
  };
}
